'use client';

import { useState, useRef } from 'react';
import Image from 'next/image';
import ScrollColorTransition from '../ui/ScrollColorTransition';

interface CaseStudyHeroProps {
  client: string;
  title: string;
  description: string;
  featuredImage: string;
  heroVideo?: string;
  backgroundColor?: string;
  textColor?: 'white' | 'black';
}

/**
 * CaseStudyHero - Hero section for case study pages
 * 
 * Displays a video as the primary hero media with an image fallback.
 * Video autoplays muted and loops. If video fails to load or is not provided,
 * the featured image is displayed instead.
 * 
 * @param client - Client name displayed as the main heading
 * @param title - Case study title displayed as subheading
 * @param description - Case study description (currently unused visually)
 * @param featuredImage - Image URL used as fallback when video unavailable
 * @param heroVideo - Optional video URL for hero background
 * @param backgroundColor - Background color for scroll transition
 * @param textColor - Text color for headings ('white' | 'black')
 */
export default function CaseStudyHero({ 
  client, 
  title, 
  description: _description, 
  featuredImage,
  heroVideo,
  backgroundColor = '#1a1a1a',
  textColor = 'white'
}: CaseStudyHeroProps) {
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const headingColorClass = textColor === 'white' ? 'text-white' : 'text-black';
  const subheadingColorClass = textColor === 'white' ? 'text-white/90' : 'text-gray-800';
  
  // Show video if provided and no error occurred
  const showVideo = heroVideo && !videoError;

  const handleVideoError = () => {
    setVideoError(true);
  };

  return (
    <>
      {/* Black to White Scroll Transition */}
      <ScrollColorTransition heroId="case-study-hero" backgroundColor={backgroundColor} />
      
      <section 
        id="case-study-hero" 
        className="relative w-full flex article-hero-padding--desktop"
        aria-label={`${client} case study hero`}
      >
        <div className="w-full mx-auto">
          {/* Hero Content - Above Image/Video */}
          <div className="mt-[6.875rem] lg:mt-[10.8125rem] mb-[2.19rem] lg:mb-[1.5rem] flex flex-col lg:flex-row lg:justify-between lg:items-end gap-4">
            <h1 className={`heading-1-lg-sm ${headingColorClass} tracking-tight`}>
              {client}
            </h1>
            
            <div className={`body-small ${subheadingColorClass} max-w-sm lg:pb-1 lg:pl-4`}>
              {title}
            </div>
          </div>

          {/* Hero Media - Video with Image fallback */}
          <div className="pb-[3.75rem] lg:pb-20 mx-auto relative w-full overflow-hidden rounded-3xl">
            {showVideo ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                onError={handleVideoError}
                className="object-cover w-full h-auto rounded-3xl"
                aria-label={`${client} hero video`}
                poster={featuredImage}
              >
                <source src={heroVideo} type="video/mp4" />
                {/* Fallback for browsers that don't support video */}
                <Image 
                  src={featuredImage} 
                  alt={client}
                  width={1200}
                  height={800}
                  className="object-cover w-full h-auto rounded-3xl"
                  priority
                />
              </video>
            ) : (
              <Image 
                src={featuredImage} 
                alt={client}
                width={1200}
                height={800}
                className="object-cover w-full h-auto rounded-3xl"
                priority
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
}
